export type NoteDiffLine = {
  text: string
  isDel: boolean
  isAdd: boolean
}

export type NoteWordToken = {
  text: string
  changed: boolean
}

export type NoteWordDiff = {
  removed: NoteWordToken[]
  added: NoteWordToken[]
}

export type NoteTextDiff = {
  lines: NoteDiffLine[]
  /** Both sides differ only in whitespace, so the diff itself is noise. */
  wsOnly: boolean
  /**
   * Present only when exactly one line was replaced by one line, which is the
   * usual shape for a note-for-patient. A line diff there is two near-identical
   * walls of text, so the renderer marks the changed words instead.
   */
  wordDiff?: NoteWordDiff
  summary: string
}

export const COLLAPSED_LINES = 6

/**
 * Stored text can arrive CRLF from a non-browser writer, and decomposed unicode
 * from a paste. Either makes the diff mark lines nobody edited as removed.
 */
const splitLines = (value: string): string[] =>
  value.length === 0
    ? []
    : value.replace(/\r\n?/g, '\n').normalize('NFC').split('\n')

/**
 * Horizontal whitespace only. Collapsing newlines too would report merging two
 * separate clinical instructions onto one line as "spacing changes only".
 */
const collapseWhitespace = (value: string): string =>
  value
    .replace(/\r\n?/g, '\n')
    .normalize('NFC')
    .split('\n')
    .map((line) => line.replace(/[^\S\n]+/g, ' ').trim())
    .join('\n')
    .trim()

/** Longest common subsequence, over lines or over words. */
const lcsTable = (before: string[], after: string[]): number[][] => {
  const table: number[][] = Array.from({ length: before.length + 1 }, () =>
    new Array<number>(after.length + 1).fill(0)
  )

  for (let i = before.length - 1; i >= 0; i -= 1) {
    for (let j = after.length - 1; j >= 0; j -= 1) {
      table[i][j] =
        before[i] === after[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1])
    }
  }

  return table
}

export const diffNoteText = (before: string, after: string): NoteTextDiff => {
  const beforeLines = splitLines(before)
  const afterLines = splitLines(after)
  const table = lcsTable(beforeLines, afterLines)

  const lines: NoteDiffLine[] = []
  let added = 0
  let removed = 0
  let i = 0
  let j = 0

  while (i < beforeLines.length && j < afterLines.length) {
    if (beforeLines[i] === afterLines[j]) {
      lines.push({ text: beforeLines[i], isDel: false, isAdd: false })
      i += 1
      j += 1
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      lines.push({ text: beforeLines[i], isDel: true, isAdd: false })
      removed += 1
      i += 1
    } else {
      lines.push({ text: afterLines[j], isDel: false, isAdd: true })
      added += 1
      j += 1
    }
  }

  while (i < beforeLines.length) {
    lines.push({ text: beforeLines[i], isDel: true, isAdd: false })
    removed += 1
    i += 1
  }

  while (j < afterLines.length) {
    lines.push({ text: afterLines[j], isDel: false, isAdd: true })
    added += 1
    j += 1
  }

  const wsOnly =
    before !== after && collapseWhitespace(before) === collapseWhitespace(after)

  const parts: string[] = []

  if (added > 0) {
    parts.push(`${added} line${added === 1 ? '' : 's'} added`)
  }

  if (removed > 0) {
    parts.push(`${removed} line${removed === 1 ? '' : 's'} removed`)
  }

  const changed = lines.filter((line) => line.isDel || line.isAdd)
  const wordDiff =
    changed.length === 2 && changed[0].isDel && changed[1].isAdd
      ? diffWords(changed[0].text, changed[1].text)
      : undefined

  return {
    lines,
    wsOnly,
    wordDiff,
    // Only changed lines are rendered, so only they decide the collapse.
    summary: parts.join(', ') || 'No change',
  }
}

/** Keeps the separators, so a rebuilt line reads exactly as it was written. */
const tokenise = (value: string): string[] =>
  value.split(/(\s+)/).filter((token) => token !== '')

const diffWords = (before: string, after: string): NoteWordDiff => {
  const beforeWords = tokenise(before)
  const afterWords = tokenise(after)
  const table = lcsTable(beforeWords, afterWords)

  const removed: NoteWordToken[] = []
  const added: NoteWordToken[] = []
  let i = 0
  let j = 0

  while (i < beforeWords.length && j < afterWords.length) {
    if (beforeWords[i] === afterWords[j]) {
      removed.push({ text: beforeWords[i], changed: false })
      added.push({ text: afterWords[j], changed: false })
      i += 1
      j += 1
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      removed.push({ text: beforeWords[i], changed: true })
      i += 1
    } else {
      added.push({ text: afterWords[j], changed: true })
      j += 1
    }
  }

  while (i < beforeWords.length) {
    removed.push({ text: beforeWords[i], changed: true })
    i += 1
  }

  while (j < afterWords.length) {
    added.push({ text: afterWords[j], changed: true })
    j += 1
  }

  return { removed, added }
}
